/**
 * RefundsTab - Admin refunds management tab
 * Full feature parity with inline implementation
 */
import { motion } from 'framer-motion';
import { RotateCcw, RefreshCw } from 'lucide-react';
import { Button } from '../../ui/button';
import { useLanguage } from '../../../context/LanguageContext';

export function RefundsTab({ 
  refunds,
  refundStats,
  onProcessRefund,
  onRefresh,
  getStatusIcon,
  getStatusColor,
  formatDate
}) {
  const { t, language } = useLanguage();

  return (
    <motion.div
      key="refunds-tab"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      className="space-y-4"
    >
      {/* Refund Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm">
          <span className="text-xs font-medium text-slate-500">{t('dashboard.status.requested')}</span>
          <p className="text-2xl font-bold text-amber-600">{refundStats?.requested || 0}</p>
        </div>
        <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm">
          <span className="text-xs font-medium text-slate-500">{t('dashboard.status.processing')}</span>
          <p className="text-2xl font-bold text-blue-600">{refundStats?.processing || 0}</p>
        </div>
        <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm">
          <span className="text-xs font-medium text-emerald-600">{t('dashboard.status.succeeded')}</span>
          <p className="text-2xl font-bold text-emerald-600">{refundStats?.succeeded || 0}</p>
        </div>
        <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm">
          <span className="text-xs font-medium text-red-600">{t('dashboard.status.failed')}</span>
          <p className="text-2xl font-bold text-red-600">{refundStats?.failed || 0}</p>
        </div>
        <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm">
          <span className="text-xs font-medium text-slate-500">{t('dashboard.status.cancelled')}</span>
          <p className="text-2xl font-bold text-slate-400">{refundStats?.cancelled || 0}</p>
        </div>
      </div>

      {/* Refunds Table */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-testid="refunds-table">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <RotateCcw className="w-5 h-5 text-emerald-600" />
            {t('dashboard.admin.refunds')}
          </h3>
          {onRefresh && (
            <Button size="sm" variant="outline" onClick={onRefresh}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          )}
        </div>
        {refunds.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            <RotateCcw className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p>{t('tables.noRefundsYet')}</p>
            <p className="text-sm mt-2">{language === 'es' ? 'Para crear un reembolso, ve a Transacciones y haz clic en una transacción' : 'To create a refund, go to Transactions and click on a transaction'}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{t('tables.refundId')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{t('common.amount')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{t('tables.reason')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{t('common.status')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{t('common.date')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {refunds.map((refund) => (
                  <tr key={refund.id} className="hover:bg-slate-50" data-testid={`refund-${refund.refund_id}`}>
                    <td className="px-6 py-4 font-mono text-xs text-slate-900">{refund.refund_id}</td>
                    <td className="px-6 py-4 font-semibold text-red-600">${refund.amount?.toFixed(2)}</td>
                    <td className="px-6 py-4 text-sm text-slate-600 max-w-xs truncate">{refund.reason}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(refund.status)}`}>
                        {getStatusIcon(refund.status)}
                        {t(`dashboard.status.${refund.status}`) || refund.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-500">{formatDate(refund.created_at)}</td>
                    <td className="px-6 py-4">
                      {refund.status === 'requested' && onProcessRefund && (
                        <Button size="sm" onClick={() => onProcessRefund(refund.refund_id)} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs">
                          {t('common.process')}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </motion.div>
  );
}

export default RefundsTab;
